import { useCallback, useMemo } from 'react';

import {
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { ColumnDef } from '@tanstack/react-table';

interface UseRowReorderProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  enableRowReorder: boolean;
  getRowId?: (row: T) => string;
  rowIdIdentifier: string;
  onReorder?: (data: T[]) => void;
  dragHandleColumnIdProp?: string;
}

interface UseRowReorderReturn {
  sortableIds: string[];
  sensors: ReturnType<typeof useSensors>;
  handleDragEnd: (event: DragEndEvent) => void;
  dragHandleColumnId: string;
}

/**
 * Hook that manages drag-and-drop row reordering using dnd-kit.
 *
 * Sets up the necessary sensors, IDs, and handlers for enabling drag-and-drop
 * reordering of table rows. Supports both pointer (mouse/touch) and keyboard
 * navigation for accessibility.
 *
 * @template T - The type of data in each row.
 * @param props - The hook configuration.
 * @returns Object containing sortable IDs, sensors, drag handler, and column ID.
 *
 * @example
 * ```tsx
 * const { sortableIds, sensors, handleDragEnd, dragHandleColumnId } = useRowReorder({
 *   data: tableData,
 *   columns: tableColumns,
 *   enableRowReorder: true,
 *   rowIdIdentifier: 'id',
 *   onReorder: (newData) => setTableData(newData)
 * });
 *
 * return (
 *   <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
 *     <SortableContext items={sortableIds}>
 *       {rows}
 *     </SortableContext>
 *   </DndContext>
 * );
 * ```
 */
export const useRowReorder = <T extends object>({
  data,
  columns,
  enableRowReorder,
  getRowId,
  rowIdIdentifier,
  onReorder,
  dragHandleColumnIdProp
}: UseRowReorderProps<T>): UseRowReorderReturn => {
  const getRowIdFromRow = useCallback(
    (row: T): string => {
      if (getRowId) {
        return String(getRowId(row));
      }
      const idFromIdentifier = (row as Record<string, string>)[rowIdIdentifier];
      if (idFromIdentifier) {
        return String(idFromIdentifier);
      }
      return '';
    },
    [getRowId, rowIdIdentifier]
  );

  const dragHandleColumnId = useMemo((): string => {
    if (dragHandleColumnIdProp) {
      return dragHandleColumnIdProp;
    }
    const firstColumnId = columns[0]?.id;
    if (firstColumnId) {
      return firstColumnId;
    }
    return '';
  }, [dragHandleColumnIdProp, columns]);

  const sortableIds = useMemo((): string[] => {
    if (!enableRowReorder) {
      return [];
    }
    return data.map(getRowIdFromRow);
  }, [enableRowReorder, data, getRowIdFromRow]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);

      const oldIndex = data.findIndex(
        (row) => getRowIdFromRow(row) === activeId
      );
      const newIndex = data.findIndex(
        (row) => getRowIdFromRow(row) === overId
      );

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      if (onReorder) {
        onReorder(arrayMove([...data], oldIndex, newIndex));
      }
    },
    [data, onReorder, getRowIdFromRow]
  );

  return {
    sortableIds,
    sensors,
    handleDragEnd,
    dragHandleColumnId
  };
};
